import { inject, Injectable } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { FeatureCollection } from 'geojson';
import { Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { environment } from '@connect-services/client/environments/environment';
import { DataPayload, IOPayload, NotificationEventEnum } from '@connect-services/client/models/notification.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ConnieGeojsonDto, StatisticToValidateDto } from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { StatisticService } from '@connect-services/client/services/statistic.service';

type MsgResolver<T> = string | ((data: T) => string | undefined);

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root',
})
export class NotificationService<T extends IOPayload> {
    public sourceRefreshNeeded: Subject<void> = new Subject<void>();

    private readonly map = new Map<RoutePathEnum, { handler: (data: T) => void; item: any }>();
    private readonly mapFilterService = inject(MapFilterService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly socket: Socket;
    private readonly statisticService = inject(StatisticService);

    public constructor() {
        this.socket = io(environment.baseWebsocketUrl, {
            autoConnect: false,
            path: environment.baseWebsocketPath,
            withCredentials: true,
        });
    }

    public connect(): void {
        if (!SecurityService.getAccessToken()) {
            return;
        }

        this.socket.auth = { token: SecurityService.getAccessToken() };

        this.socket.connect();

        this.socket.once('open', () => {
            // eslint-disable-next-line no-console
            console.log(`Connected to ${environment.baseWebsocketUrl}`);
        });

        this.registerNotificationsOnce();
    }

    public disconnect(): void {
        this.socket.disconnect();
    }

    public off<T extends DataPayload>(event: NotificationEventEnum, listener: (data: T) => void): void {
        this.socket.off(event, listener);
    }

    public on<T extends DataPayload>(event: NotificationEventEnum, listener: (data: T) => void): void {
        this.socket.on(event, (data: T) => {
            listener(data);
            if (
                event === NotificationEventEnum.DEPOSIT_VALIDATION &&
                data['error'] === false &&
                data['status'] === 'done'
            ) {
                this.sourceRefreshNeeded.next();
            }
        });
    }

    public once<T extends DataPayload>(event: NotificationEventEnum, listener: (data: T) => void): void {
        this.socket.once(event, listener);
    }

    public register(
        event: NotificationEventEnum,
        translocoKeySuccess: MsgResolver<T>,
        translocoKeyError: MsgResolver<T>,
        sidebarItem?: any,
        isTranslationNeeded?: boolean,
    ): void {
        const handler = (data: T): void =>
            this.registerHandler(data, translocoKeySuccess, translocoKeyError, sidebarItem, isTranslationNeeded);
        this.socket.on(event, handler);

        if (!sidebarItem) {
            return;
        }

        AssertService.isEnum<RoutePathEnum>(sidebarItem.path[0], RoutePathEnum);

        const mapValue = this.map.get(sidebarItem?.path[0]);
        if (mapValue) {
            this.socket.off(event, mapValue.handler);
        }

        this.map.set(sidebarItem.path[0], {
            handler: handler,
            item: sidebarItem,
        });
    }

    public requestPermission(): void {
        if (Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }

    private registerHandler(
        data: T,
        translocoKeySuccess: MsgResolver<T>,
        translocoKeyError: MsgResolver<T>,
        sidebarItem?: any,
        isTranslationNeeded?: boolean,
    ): void {
        const key: string | undefined = data.error
            ? typeof translocoKeyError === 'string'
                ? translocoKeyError
                : translocoKeyError?.(data)
            : typeof translocoKeySuccess === 'string'
              ? translocoKeySuccess
              : translocoKeySuccess?.(data);

        if (!key) {
            return;
        }

        new Notification(isTranslationNeeded ? translate(key) : key, {
            tag: data?.id?.toString(),
        });

        if (!sidebarItem) {
            return;
        }

        sidebarItem.hasNotification.set(true);
    }

    private registerNotificationsOnce(): void {
        // Map Filter deletion
        this.register(
            NotificationEventEnum.MAP_FILTER_DELETED,
            (data: IOPayload) => {
                const [filterId, projectId] = data!.id! as unknown as [string, string];
                if (this.projectBloc.value()!.id !== projectId) {
                    return;
                }

                const { filterName, wasActive } = this.mapFilterService.deleteLocalFilter(filterId.toString());

                return this.mapFilterService.buildTooltipMessageOnDelete(filterName, wasActive);
            },
            () => {
                return undefined;
            },
            false,
        );

        //Import Statistic From CONNIE
        this.register(
            NotificationEventEnum.CONNIE_STATISTIC_TO_VALIDATE,
            (data: IOPayload): undefined => {
                this.statisticService.openNewStatisticModal(data as StatisticToValidateDto);

                return undefined;
            },
            () => {
                return undefined;
            },
            false,
        );

        //Import GeoJSON From CONNIE
        this.register(
            NotificationEventEnum.CONNIE_GEOJSON,
            (data: IOPayload): undefined => {
                const payload: ConnieGeojsonDto = data as ConnieGeojsonDto;
                const currentSessionId: string | null = sessionStorage.getItem('session');

                if (payload.sessionId !== currentSessionId) {
                    return undefined;
                }

                this.mapSourceService.drawConnieGeojson(payload.geojson as FeatureCollection);

                return undefined;
            },
            () => {
                return undefined;
            },
            false,
        );
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
